import {
  buildCustomAttributeField,
  buildSchemaAttributeField,
  DEFAULT_FILTER_OPERATOR,
  FIXED_DEMOGRAPHIC_FIELDS,
  formatFieldLabel,
  GENERATED_MAPPED_KEYS,
  getFilterField,
  getFilterOptionLabel,
  getStaticFilterFields,
  INCOME_LEVELS,
  INTEREST_DEVICE_FIELDS,
  isFixedDemographicKey,
  isSchemaBuildableAttributeKey,
  normalizeFieldKey,
  PERSONA_METADATA_KEYS,
  SEARCH_FIELDS,
  STATIC_TOP_LEVEL_ATTRIBUTE_KEYS,
} from '@/constants/filterSchema';
import {
  buildPersonaCountryOptions,
  normalizeCountryName,
  resolveCanonicalCountry,
  sanitizeCountryValues,
} from '@/utils/countryList';
import { resolveIncomeLevelIndex as resolveIncomeLevelIndexFromValue } from '@/utils/incomeLevelResolver';
import { extractSchemaChoices, mergeToggleOptions } from '@/utils/toggleFieldOptions';
import { combineFilterMatches } from '@/utils/combineFilterMatches';

function normalizeText(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function generatedDataToObject(persona) {
  if (!persona?.data) return {};
  if (Array.isArray(persona.data)) {
    return Object.fromEntries(
      persona.data
        .filter((entry) => entry && entry.key != null)
        .map((entry) => [entry.key, entry.value]),
    );
  }
  return persona.data;
}

function valuesToArray(value) {
  if (Array.isArray(value)) return value.filter((item) => item != null && item !== '');
  if (value == null || value === '') return [];
  return [value];
}

function isScalarValue(value) {
  return ['string', 'number', 'boolean'].includes(typeof value);
}

function buildCustomAttributes(persona, personalCharacteristics, isGenerated, data) {
  const custom = {};

  Object.entries(personalCharacteristics || {}).forEach(([key, value]) => {
    if (!isFixedDemographicKey(key) && isScalarValue(value)) {
      custom[normalizeFieldKey(key)] = value;
    }
  });

  if (!isGenerated) {
    STATIC_TOP_LEVEL_ATTRIBUTE_KEYS.forEach((key) => {
      if (persona?.[key] != null && isScalarValue(persona[key])) {
        custom[normalizeFieldKey(key)] = persona[key];
      }
    });
  } else {
    Object.entries(data || {}).forEach(([key, value]) => {
      if (!GENERATED_MAPPED_KEYS.has(key) && isScalarValue(value)) {
        custom[normalizeFieldKey(key)] = value;
      }
    });
  }

  return custom;
}

export function normalizePersonaForFilter(persona, isGenerated = false) {
  if (!persona) return null;

  if (isGenerated) {
    const data = generatedDataToObject(persona);
    const firstName = data.first_name || '';
    const lastName = data.last_name || '';
    const personalCharacteristics = {
      age: data.age,
      gender: data.gender,
      occupation: data.job_title || data.occupation,
      civil_status: data.marital_status || data.civil_status,
      geographic_area_of_residence: data.area || data.geographic_area_of_residence,
      qualification: data.education_level || data.qualification,
      income: data.annual_income || data.income,
      country: data.country || data.area,
    };

    return {
      id: persona.persona_id,
      title: `${firstName} ${lastName}`.trim() || persona.name || 'Generated Persona',
      name: persona.name || `${firstName} ${lastName}`.trim(),
      tags: valuesToArray(persona.tags),
      personal_characteristics: personalCharacteristics,
      personal_interests: Array.isArray(data.personal_interests)
        ? data.personal_interests
        : Array.isArray(data.interests)
          ? data.interests
          : [],
      devices: Array.isArray(data.devices) ? data.devices : [],
      custom_attributes: buildCustomAttributes(persona, personalCharacteristics, true, data),
      _raw: persona,
    };
  }

  const rawPersonalCharacteristics = {
    ...(persona.personal_characteristics || {}),
    ...(persona.personalCharacteristics || {}),
  };

  // Some domains' persona records store personal_characteristics keys in
  // camelCase (e.g. `civilStatus`) while every filter field key is the
  // canonical snake_case form (normalizeFieldKey). Without this, lookups by
  // the canonical key (getFieldValue, collectDistinctOptions, schema bounds
  // resolution, ...) silently miss the value even though it exists — the
  // Top Management domain's Civil Status previously showed no data at all
  // because of exactly this mismatch. Re-keying once here, generically, for
  // every attribute fixes it for civil_status and any other camelCase key.
  const personalCharacteristics = {};
  Object.entries(rawPersonalCharacteristics).forEach(([key, value]) => {
    personalCharacteristics[normalizeFieldKey(key)] = value;
  });

  if (!personalCharacteristics.country && persona.country) {
    personalCharacteristics.country = persona.country;
  }

  return {
    id: persona.id ?? persona.persona_id ?? persona.name,
    title: persona.title || persona.name,
    name: persona.name || persona.title,
    tags: valuesToArray(persona.tags),
    personal_characteristics: personalCharacteristics,
    personal_interests: persona.personal_interests || persona.personalInterests || [],
    devices: Array.isArray(persona.devices)
      ? persona.devices
      : Array.isArray(persona.currentTechnologyAndTools)
        ? persona.currentTechnologyAndTools
        : [],
    custom_attributes: buildCustomAttributes(persona, personalCharacteristics, false, null),
    _raw: persona,
  };
}

export function discoverCustomAttributeFields(
  personas,
  isGenerated = false,
  schemaAttributes = null,
) {
  const discovered = new Set();

  (personas || []).forEach((persona) => {
    const normalized = normalizePersonaForFilter(persona, isGenerated);
    Object.keys(normalized.custom_attributes || {}).forEach((key) => discovered.add(key));
  });

  (schemaAttributes || []).forEach((attr) => {
    if (!attr?.key || isFixedDemographicKey(attr.key)) return;
    discovered.add(normalizeFieldKey(attr.key));
  });

  return Array.from(discovered)
    .sort((a, b) => a.localeCompare(b))
    .map((key) => buildCustomAttributeField(key, schemaAttributes));
}

// Numeric bound derived from actual persona values for a field — used only
// to fill in a `number` schema attribute's min/max when the schema itself
// doesn't provide one. Mirrors collectDistinctOptions' persona-scanning
// approach but keeps only values that parse as finite numbers.
function derivePersonaNumberBounds(personas, fieldKey, isGenerated) {
  let min = null;
  let max = null;

  (personas || []).forEach((persona) => {
    const normalized = normalizePersonaForFilter(persona, isGenerated);
    valuesToArray(getFieldValue(normalized, fieldKey)).forEach((value) => {
      const num = Number(value);
      if (!Number.isFinite(num)) return;
      if (min === null || num < min) min = num;
      if (max === null || num > max) max = num;
    });
  });

  return min === null || max === null ? null : { min, max };
}

// Generic fallback bounds for a schema `number` attribute when neither the
// schema (`options.min` / `options.max`) nor any persona value supplies one.
// Mirrors the backend's own generic-integer default (min: 0, max: 999 in
// legacy_field_to_attribute, persona_domain_schema_defaults.py) — an
// existing project convention, not an invented range — so a schema-defined
// numeric attribute still renders as a usable range slider instead of
// disappearing or silently falling back to a value-list multiselect.
const DEFAULT_NUMBER_FIELD_BOUNDS = { min: 0, max: 999 };

const FILTER_FIELD_TYPE_RANK = {
  income_range: 30,
  toggle: 20,
  multiselect: 10,
  range: 5,
  country_select: 5,
  text: 1,
  tags: 1,
};

function filterFieldTypeRank(field) {
  return FILTER_FIELD_TYPE_RANK[field?.type] ?? 0;
}

function preferFilterField(existing, candidate, key) {
  if (key === 'income') {
    return filterFieldTypeRank(candidate) > filterFieldTypeRank(existing)
      ? candidate
      : existing;
  }
  return existing;
}

function coerceRangeDraftValues(values, min, max) {
  const safeMin = Number.isFinite(min) ? min : 0;
  const safeMax = Number.isFinite(max) ? max : safeMin;
  const lo = Number(Array.isArray(values) ? values[0] : undefined);
  const hi = Number(Array.isArray(values) ? values[1] : undefined);
  return [
    Number.isFinite(lo) ? lo : safeMin,
    Number.isFinite(hi) ? hi : safeMax,
  ];
}

export function isValidDraftEntry(field, entry) {
  if (!field || !entry || !Array.isArray(entry.values)) return false;

  if (field.type === 'range') {
    return entry.values.length >= 2
      && Number.isFinite(Number(entry.values[0]))
      && Number.isFinite(Number(entry.values[1]));
  }

  if (field.type === 'income_range') {
    return entry.values.length >= 2
      && Number.isFinite(Number(entry.values[0]))
      && Number.isFinite(Number(entry.values[1]));
  }

  if (field.type === 'text') {
    return entry.values.length >= 1;
  }

  return true;
}

export function normalizeDraftEntryForField(field, entry) {
  const operator = entry?.operator === 'or' || entry?.operator === 'and'
    ? entry.operator
    : (field.defaultOperator || DEFAULT_FILTER_OPERATOR);

  if (field.type === 'range') {
    const min = Number.isFinite(field.min) ? field.min : 0;
    const max = Number.isFinite(field.max) ? field.max : min;
    return {
      operator,
      values: coerceRangeDraftValues(entry?.values, min, max),
    };
  }

  if (field.type === 'income_range') {
    const maxIndex = (field.levels || INCOME_LEVELS).length - 1;
    return {
      operator,
      values: coerceRangeDraftValues(entry?.values, 0, maxIndex),
    };
  }

  if (field.type === 'text') {
    const value = Array.isArray(entry?.values) ? entry.values[0] : '';
    return { operator, values: [String(value ?? '')] };
  }

  const values = Array.isArray(entry?.values)
    ? entry.values.filter((value) => value != null && value !== '')
    : [];
  return { operator, values };
}

// Resolves the final min/max for a schema-driven `range` field. Per bound,
// in order of preference: schema value -> value derived from numeric persona
// data -> the generic fallback above. Always returns a usable numeric
// { min, max }: a schema `number` attribute must never be dropped just
// because its bounds aren't known yet.
function resolveNumberFieldBounds(field, personas, isGenerated) {
  let { min, max } = field;

  if (typeof min !== 'number' || typeof max !== 'number') {
    const derived = derivePersonaNumberBounds(personas, field.key, isGenerated);
    if (typeof min !== 'number') min = derived ? derived.min : undefined;
    if (typeof max !== 'number') max = derived ? derived.max : undefined;
  }

  if (typeof min !== 'number') min = DEFAULT_NUMBER_FIELD_BOUNDS.min;
  if (typeof max !== 'number') max = DEFAULT_NUMBER_FIELD_BOUNDS.max;
  if (min > max) max = min;

  return { min, max };
}

// Builds filter fields directly from the Persona Schema (GET
// .../persona-schema), so a defined attribute appears in the filter UI as
// soon as it exists in the schema — even before any persona has a value for
// it — with the type-appropriate control (range/multiselect/text) instead
// of always defaulting to a value-list multiselect. This applies to EVERY
// declared attribute, including the fixed/system demographic keys (age,
// gender, income, country, ...) — see isSchemaBuildableAttributeKey /
// SCHEMA_DRIVEN_FIXED_KEYS, which covers the full FIXED_DEMOGRAPHIC_KEYS set.
// A schema `number` attribute always yields a `range` field (bounds are
// resolved, never dropped) so a type change from the Framework Editor is
// always reflected.
export function buildAttributeFieldsFromSchema(schemaAttributes, personas, isGenerated = false) {
  const sorted = [...(schemaAttributes || [])]
    .filter((attr) => attr?.key && isSchemaBuildableAttributeKey(attr.key))
    .sort((a, b) => (Number(a.display_order) || 0) - (Number(b.display_order) || 0));

  const byKey = new Map();

  sorted.forEach((attr) => {
    let field = buildSchemaAttributeField(attr);
    if (field.type === 'range') {
      const bounds = resolveNumberFieldBounds(field, personas, isGenerated);
      field = { ...field, min: bounds.min, max: bounds.max };
    }

    const { key } = field;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, field);
      return;
    }
    byKey.set(key, preferFilterField(existing, field, key));
  });

  return Array.from(byKey.values());
}

// The set of keys the schema is authoritative for — computed from the
// DECLARED attributes, not just the fields that rendered, so a schema
// attribute is never re-added by data discovery (or kept as its old fixed
// control) just because a bound/option could not be resolved this pass.
function schemaDefinedKeySet(schemaAttributes = []) {
  return new Set(
    (schemaAttributes || [])
      .filter((attr) => attr?.key && isSchemaBuildableAttributeKey(attr.key))
      .map((attr) => normalizeFieldKey(attr.key)),
  );
}

// schemaAttributes is optional so every existing caller (data-only
// filtering) keeps working unchanged. When provided, a schema attribute's
// declared type is authoritative for EVERY key it covers — fixed/system
// (age, gender, income, country, ...) or custom — and its schema-built
// field replaces the matching fixed/static field AND suppresses data-driven
// discovery for that key. discoverCustomAttributeFields (rename-tracking
// aware — resolveSchemaAttributeByKey / previous_keys) still runs for any
// custom attribute the schema doesn't (yet) declare. A fixed key with no
// schema entry for this domain keeps its bespoke control (gender/civil_status
// keep the `toggle` widget, with options filled from schema choices merged
// with distinct persona values — B3 — since there's no schema type to trust
// for that domain).
export function getAllFilterFields(personas, isGenerated = false, schemaAttributes = null) {
  const attrs = schemaAttributes || [];
  const schemaFields = buildAttributeFieldsFromSchema(attrs, personas, isGenerated);
  const schemaKeys = schemaDefinedKeySet(attrs);

  const discoveredFields = discoverCustomAttributeFields(personas, isGenerated, attrs)
    .filter((field) => !schemaKeys.has(field.key));

  const staticFields = getStaticFilterFields()
    .filter((field) => !schemaKeys.has(field.key));

  const fields = [...staticFields, ...schemaFields, ...discoveredFields];

  return fields.map((field) => {
    if (field.type !== 'toggle') return field;
    return {
      ...field,
      options: resolveToggleFieldOptions(
        field.key,
        personas,
        isGenerated,
        attrs,
      ),
    };
  });
}

/**
 * Toggle option lists for gender / civil_status (B3):
 * prefer schema choices that can match current personas, then any extra
 * distinct values; if no schema, use collectDistinctOptions like multiselect.
 */
export function resolveToggleFieldOptions(
  fieldKey,
  personas,
  isGenerated = false,
  schemaAttributes = null,
) {
  const distinct = collectDistinctOptions(personas, fieldKey, isGenerated);
  const schemaChoices = extractSchemaChoices(fieldKey, schemaAttributes, normalizeFieldKey);
  return mergeToggleOptions(schemaChoices, distinct);
}


function getFieldValue(normalizedPersona, fieldKey) {
  if (fieldKey === 'personal_interests') {
    return normalizedPersona.personal_interests || [];
  }
  if (fieldKey === 'devices') {
    return normalizedPersona.devices || [];
  }
  if (fieldKey === 'persona_tags') {
    return normalizedPersona.tags || [];
  }
  if (fieldKey === 'name_search') {
    return [
      normalizedPersona.title,
      normalizedPersona.name,
    ].filter(Boolean);
  }

  if (normalizedPersona.custom_attributes?.[fieldKey] != null) {
    return normalizedPersona.custom_attributes[fieldKey];
  }

  return normalizedPersona.personal_characteristics?.[fieldKey];
}

function parseAgeRange(value) {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return { min: value, max: value };
  }
  if (typeof value === 'string') {
    const rangeMatch = value.match(/(\d+)\s*[-–+]\s*(\d+)/);
    if (rangeMatch) {
      return { min: Number(rangeMatch[1]), max: Number(rangeMatch[2]) };
    }
    const single = Number.parseInt(value, 10);
    if (!Number.isNaN(single)) {
      return { min: single, max: single };
    }
  }
  return null;
}

function ageRangesOverlap(personaAgeValue, [selectedMin, selectedMax]) {
  const personaRange = parseAgeRange(personaAgeValue);
  if (!personaRange) return false;
  return personaRange.max >= selectedMin && personaRange.min <= selectedMax;
}

export function resolveIncomeLevelIndex(value) {
  return resolveIncomeLevelIndexFromValue(value, INCOME_LEVELS);
}

function incomeWithinRange(personaIncomeValue, [minIndex, maxIndex]) {
  const index = resolveIncomeLevelIndex(personaIncomeValue);
  if (index == null) return false;
  return index >= minIndex && index <= maxIndex;
}

/**
 * Exact match for discrete choice fields: toggle, multiselect, tags,
 * schema single_select, interests, devices, etc.
 * Avoids substring false positives (e.g. "male" matching "female").
 */
function discreteValuesMatch(selectedValues, personaValue) {
  const personaArray = valuesToArray(personaValue).map(normalizeText);
  if (!personaArray.length) return false;

  return (selectedValues || []).some((selected) => {
    const normalizedSelected = normalizeText(selected);
    if (!normalizedSelected) return false;
    return personaArray.includes(normalizedSelected);
  });
}

/**
 * One-way partial match for free-text search fields only (name search, text_short).
 * Haystack includes needle — never the reverse, to avoid false positives.
 */
function partialTextMatches(selectedValues, personaValue) {
  const personaArray = valuesToArray(personaValue).map(normalizeText);
  if (!personaArray.length) return false;

  return (selectedValues || []).some((selected) => {
    const needle = normalizeText(selected);
    if (!needle) return false;
    return personaArray.some((candidate) => candidate.includes(needle));
  });
}

/** Field types / keys that represent picked values, not fuzzy search. */
function usesDiscreteValueMatching(field) {
  if (!field) return true;
  if (field.type === 'text') return false;
  if (['toggle', 'multiselect', 'tags', 'country_select', 'income_range', 'range'].includes(field.type)) {
    return true;
  }
  return [
    'personal_interests',
    'devices',
    'persona_tags',
    'geographic_area_of_residence',
  ].includes(field.key);
}

function matchesCriterion(normalizedPersona, criterion, allFields) {
  const field = getFilterField(criterion.field, allFields);
  if (!field || !criterion.values?.length) return true;

  const personaValue = getFieldValue(normalizedPersona, criterion.field);

  if (field.type === 'text') {
    const query = normalizeText(criterion.values[0]);
    if (!query) return true;
    return partialTextMatches(criterion.values, personaValue);
  }

  if (field.type === 'range') {
    const [min, max] = criterion.values;
    return ageRangesOverlap(personaValue, [min, max]);
  }

  if (field.type === 'income_range') {
    const [minIndex, maxIndex] = criterion.values;
    return incomeWithinRange(personaValue, [minIndex, maxIndex]);
  }

  if (field.type === 'country_select' || field.key === 'country') {
    const personaValues = valuesToArray(personaValue).map((value) =>
      normalizeCountryName(resolveCanonicalCountry(value) || value),
    );
    return criterion.values.some((selected) => {
      const normalizedSelected = normalizeCountryName(
        resolveCanonicalCountry(selected) || selected,
      );
      return personaValues.includes(normalizedSelected);
    });
  }

  if (usesDiscreteValueMatching(field)) {
    return discreteValuesMatch(criterion.values, personaValue);
  }

  return discreteValuesMatch(criterion.values, personaValue);
}

function orderActiveCriteriaByPanel(criteria, allFields) {
  const orderIndex = new Map(
    getFieldsInPanelOrder(allFields).map((field, index) => [field.key, index]),
  );

  return (criteria || [])
    .filter((criterion) => isActiveCriterion(criterion, allFields))
    .sort((a, b) => (orderIndex.get(a.field) ?? 999) - (orderIndex.get(b.field) ?? 999));
}

export function evaluateFilter(normalizedPersona, criteria, allFields = getStaticFilterFields()) {
  const activeCriteria = orderActiveCriteriaByPanel(criteria, allFields);

  if (!activeCriteria.length) return true;

  const results = activeCriteria.map((criterion) => ({
    matches: matchesCriterion(normalizedPersona, criterion, allFields),
    operator: (criterion.operator || DEFAULT_FILTER_OPERATOR) === 'or' ? 'or' : 'and',
  }));

  return combineFilterMatches(results);
}

export function filterPersonas(personas, criteria, isGenerated = false, allFields = null) {
  const list = personas || [];
  const fields = allFields || getAllFilterFields(list, isGenerated);
  const activeCriteria = (criteria || []).filter((criterion) => isActiveCriterion(criterion, fields));

  if (!activeCriteria.length) return list;

  return list.filter((persona) => {
    const normalized = normalizePersonaForFilter(persona, isGenerated);
    return evaluateFilter(normalized, activeCriteria, fields);
  });
}

export function countMatchingPersonas(personas, criteria, isGenerated = false, allFields = null) {
  return filterPersonas(personas, criteria, isGenerated, allFields).length;
}

export function collectDistinctOptions(personas, fieldKey, isGenerated = false) {
  const values = new Set();

  (personas || []).forEach((persona) => {
    const normalized = normalizePersonaForFilter(persona, isGenerated);
    const rawValue = getFieldValue(normalized, fieldKey);

    valuesToArray(rawValue).forEach((value) => {
      const text = String(value ?? '').trim();
      if (text) values.add(text);
    });
  });

  return Array.from(values).sort((a, b) => a.localeCompare(b));
}

export function buildAvailableCountryOptions(personas, isGenerated = false) {
  const rawCountries = collectDistinctOptions(personas, 'country', isGenerated);
  return buildPersonaCountryOptions(rawCountries);
}

export function formatCriterionLabel(criterion, allFields = getStaticFilterFields(), t = (key) => key) {
  const field = getFilterField(criterion.field, allFields);
  if (!field || !criterion.values?.length) return '';

  const label = t(field.label);

  if (field.type === 'range') {
    const unit = field.unit || '';
    const rangeText = unit
      ? `${criterion.values[0]}-${criterion.values[1]} ${unit}`
      : `${criterion.values[0]}-${criterion.values[1]}`;
    return `${label}: ${rangeText}`;
  }

  if (field.type === 'income_range') {
    const [minIndex, maxIndex] = criterion.values;
    const minLabel = getFilterOptionLabel(t, INCOME_LEVELS[minIndex]) || minIndex;
    const maxLabel = getFilterOptionLabel(t, INCOME_LEVELS[maxIndex]) || maxIndex;
    return `${label}: ${minLabel} – ${maxLabel}`;
  }

  if (field.type === 'text') {
    return `${label}: ${criterion.values[0]}`;
  }

  // Toggle options may be dynamic (schema / distinct values). Translate when
  // a known i18n key exists; otherwise show the raw value.
  const displayValues = field.type === 'toggle'
    ? criterion.values.map((v) => getFilterOptionLabel(t, v))
    : criterion.values;

  if (displayValues.length === 1) {
    return `${label}: ${displayValues[0]}`;
  }

  return `${label}: ${displayValues.join(', ')}`;
}

function isActiveCriterion(criterion, allFields) {
  if (!Array.isArray(criterion.values) || !criterion.values.length) {
    return false;
  }

  const field = getFilterField(criterion.field, allFields);
  if (!field) return false;

  if (field.type === 'text') {
    return Boolean(String(criterion.values[0] || '').trim());
  }

  if (field.type === 'range') {
    return (
      criterion.values.length === 2
      && (criterion.values[0] > field.min || criterion.values[1] < field.max)
    );
  }

  if (field.type === 'income_range') {
    const maxIndex = (field.levels || INCOME_LEVELS).length - 1;
    return (
      criterion.values.length === 2
      && (criterion.values[0] > 0 || criterion.values[1] < maxIndex)
    );
  }

  return criterion.values.length > 0;
}

export function hasActiveCriteria(criteria = [], allFields = getStaticFilterFields()) {
  return (criteria || []).some((criterion) => isActiveCriterion(criterion, allFields));
}

export function createEmptyDraft(allFields = getStaticFilterFields()) {
  const draft = {};

  allFields.forEach((field) => {
    if (field.type === 'range') {
      draft[field.key] = {
        values: [field.min, field.max],
        operator: field.defaultOperator || DEFAULT_FILTER_OPERATOR,
      };
      return;
    }

    if (field.type === 'income_range') {
      const maxIndex = (field.levels || INCOME_LEVELS).length - 1;
      draft[field.key] = {
        values: [0, maxIndex],
        operator: field.defaultOperator || DEFAULT_FILTER_OPERATOR,
      };
      return;
    }

    if (field.type === 'text') {
      draft[field.key] = {
        values: [''],
        operator: field.defaultOperator || DEFAULT_FILTER_OPERATOR,
      };
      return;
    }

    draft[field.key] = {
      values: [],
      operator: field.defaultOperator || DEFAULT_FILTER_OPERATOR,
    };
  });

  return draft;
}

export function applyCriteriaToDraft(appliedCriteria = [], allFields = getStaticFilterFields()) {
  const draft = createEmptyDraft(allFields);

  (appliedCriteria || []).forEach((criterion) => {
    const normalized = normalizeStoredCriterion(criterion);
    if (!normalized || !draft[normalized.field]) return;
    draft[normalized.field].values = [...normalized.values];
    draft[normalized.field].operator = normalized.operator || draft[normalized.field].operator;
  });

  return draft;
}

/**
 * M10 (light): normalize legacy / alternate criterion shapes to
 * { field, values, operator } used by browser-side filtering.
 */
export function normalizeStoredCriterion(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const field = raw.field || raw.key || raw.attribute || raw.name;
  if (!field) return null;

  let values = raw.values;
  if (!Array.isArray(values)) {
    if (raw.value !== undefined && raw.value !== null) {
      values = Array.isArray(raw.value) ? raw.value : [raw.value];
    } else if (raw.min !== undefined || raw.max !== undefined) {
      values = [raw.min, raw.max];
    } else {
      values = [];
    }
  }

  const operator = (raw.operator || raw.op || DEFAULT_FILTER_OPERATOR).toString().toLowerCase() === 'or'
    ? 'or'
    : 'and';

  return { field: String(field), values: [...values], operator };
}

/**
 * S4: keep criteria whose field still exists; skip missing/renamed fields
 * and report them so the UI can warn instead of dropping silently.
 *
 * When schemaAttributes is provided, saved filters that reference a key listed
 * in an attribute's previous_keys are remapped to the attribute's current key.
 */
export function buildPreviousKeyMap(schemaAttributes = []) {
  const map = new Map();
  (schemaAttributes || []).forEach((attr) => {
    const currentKey = attr?.key;
    if (!currentKey) return;
    (attr.previous_keys || []).forEach((oldKey) => {
      if (oldKey && !map.has(oldKey)) {
        map.set(oldKey, currentKey);
      }
    });
  });
  return map;
}

export function reconcileFilterCriteria(
  criteria = [],
  allFields = getStaticFilterFields(),
  schemaAttributes = null,
) {
  const fieldKeys = new Set((allFields || []).map((field) => field.key));
  const previousKeyMap = buildPreviousKeyMap(schemaAttributes);
  const kept = [];
  const droppedFields = [];

  (criteria || []).forEach((raw) => {
    const criterion = normalizeStoredCriterion(raw);
    if (!criterion) return;

    let field = criterion.field;
    if (!fieldKeys.has(field)) {
      const remapped = previousKeyMap.get(field);
      if (remapped && fieldKeys.has(remapped)) {
        field = remapped;
      }
    }

    if (!fieldKeys.has(field)) {
      if (!droppedFields.includes(criterion.field)) {
        droppedFields.push(criterion.field);
      }
      return;
    }

    kept.push(field === criterion.field ? criterion : { ...criterion, field });
  });

  return { criteria: kept, droppedFields };
}

export function formatDroppedFilterFieldsMessage(droppedFields = [], t = (key, params) => key) {
  if (!droppedFields.length) return '';
  const list = droppedFields.join(', ');
  const translated = t('filtersettings.droppedUnavailableFields', { fields: list });
  if (translated && translated !== 'filtersettings.droppedUnavailableFields') {
    return translated;
  }
  return `Some filter fields are no longer available and were skipped: ${list}`;
}

export function getPanelSections(allFields) {
  // title is an i18n KEY (see the note in filterSchema.js) — the component
  // must call t(section.title) to render it, same as field.label.
  const sections = [
    { key: 'demographics', title: 'filtersettings.sectionDemographics', fields: getFilterFieldsByCategory(allFields, 'demographics') },
    { key: 'attributes', title: 'filtersettings.sectionDomainAttributes', fields: getFilterFieldsByCategory(allFields, 'attributes') },
    { key: 'search', title: 'filtersettings.sectionSearch', fields: getFilterFieldsByCategory(allFields, 'search') },
    { key: 'interests', title: 'personaedit.fields.interests', fields: getFilterFieldsByCategory(allFields, 'interests') },
    { key: 'devices', title: 'filtersettings.devices', fields: getFilterFieldsByCategory(allFields, 'devices') },
  ];

  return sections.filter(
    (section) => section.key === 'attributes' || section.fields.length > 0,
  );
}

function getFilterFieldsByCategory(allFields, category) {
  return allFields.filter((field) => field.category === category);
}

/** Field order matches the filter dialog sections top-to-bottom. */
export function getFieldsInPanelOrder(allFields = getStaticFilterFields()) {
  return getPanelSections(allFields).flatMap((section) => section.fields);
}

export function draftToCriteria(draft = {}, allFields = getStaticFilterFields()) {
  return getFieldsInPanelOrder(allFields)
    .map((field) => ({
      field: field.key,
      values: Array.isArray(draft[field.key]?.values) ? [...draft[field.key].values] : [],
      operator: draft[field.key]?.operator || field.defaultOperator || DEFAULT_FILTER_OPERATOR,
    }))
    .filter((criterion) => isActiveCriterion(criterion, allFields));
}

function collectPersonaLookupKeys(persona, isGenerated = false) {
  const keys = new Set();
  const normalized = normalizePersonaForFilter(persona, isGenerated);
  const addKey = (value) => {
    const text = normalizeText(value);
    if (text) keys.add(text);
  };

  addKey(normalized.title);
  addKey(normalized.name);
  addKey(persona?.title);
  addKey(persona?.name);
  addKey(persona?.persona_id);

  return keys;
}

export function filterChatPersonaOptions(
  apiPersonas = [],
  {
    staticPersonas = [],
    generatedPersonas = [],
    criteria = [],
    schemaAttributes = [],
  } = {},
) {
  const staticFields = getAllFilterFields(staticPersonas, false, schemaAttributes);
  const generatedFields = getAllFilterFields(generatedPersonas, true, schemaAttributes);

  if (!hasActiveCriteria(criteria, staticFields) && !hasActiveCriteria(criteria, generatedFields)) {
    return apiPersonas;
  }

  const allowedKeys = new Set();

  filterPersonas(staticPersonas, criteria, false, staticFields).forEach((persona) => {
    collectPersonaLookupKeys(persona, false).forEach((key) => allowedKeys.add(key));
  });

  filterPersonas(generatedPersonas, criteria, true, generatedFields).forEach((persona) => {
    collectPersonaLookupKeys(persona, true).forEach((key) => allowedKeys.add(key));
  });

  return apiPersonas.filter((persona) => {
    const lookupKeys = collectPersonaLookupKeys(persona, false);
    lookupKeys.add(normalizeText(persona?.persona_id));
    lookupKeys.add(normalizeText(persona?.name));

    for (const key of lookupKeys) {
      if (allowedKeys.has(key)) {
        return true;
      }
    }

    return false;
  });
}

export function countMatchingChatPersonaOptions(
  apiPersonas = [],
  options = {},
) {
  return filterChatPersonaOptions(apiPersonas, options).length;
}

export {
  FIXED_DEMOGRAPHIC_FIELDS,
  SEARCH_FIELDS,
  INTEREST_DEVICE_FIELDS,
  formatFieldLabel,
  sanitizeCountryValues,
};
